import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";

interface PageJumperProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

// Helper function for pagination
const generatePaginationPages = (currentPage: number, totalPages: number, pagesToShow: number = 5): (number | string)[] => {
    const pages: (number | string)[] = [];
    const halfPagesToShow = Math.floor(pagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfPagesToShow);
    let endPage = Math.min(totalPages, currentPage + halfPagesToShow);

    if (endPage - startPage + 1 < pagesToShow) {
        if (startPage === 1) {
            endPage = Math.min(totalPages, startPage + pagesToShow - 1);
        } else if (endPage === totalPages) {
            startPage = Math.max(1, totalPages - pagesToShow + 1);
        }
    }

    // Always show the first page
    if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
            pages.push("...");
        }
    }

    // Show pages around the current page
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    // Always show the last page
    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            pages.push("...");
        }
        pages.push(totalPages);
    }

    return Array.from(new Set(pages)); // Remove duplicates in case of small totalPages
};

export function PageJumper({ currentPage, totalPages, onPageChange }: PageJumperProps) {
    const [jumpPage, setJumpPage] = useState<string>(currentPage.toString());

    const handleJump = () => {
        const pageNum = parseInt(jumpPage, 10);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
            onPageChange(pageNum);
            setJumpPage(pageNum.toString());
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleJump();
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <Pagination>
                <PaginationContent>
                    <PaginationItem>
                        <PaginationPrevious
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onPageChange(Math.max(1, currentPage - 1));
                            }}
                            className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                        />
                    </PaginationItem>
                    {generatePaginationPages(currentPage, totalPages).map((page, index) => (
                        <PaginationItem key={index}>
                            {page === "..." ? (
                                <PaginationEllipsis />
                            ) : (
                                <PaginationLink
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onPageChange(page as number);
                                    }}
                                    isActive={page === currentPage}
                                >
                                    {page}
                                </PaginationLink>
                            )}
                        </PaginationItem>
                    ))}
                    <PaginationItem>
                        <PaginationNext
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onPageChange(Math.min(totalPages, currentPage + 1));
                            }}
                            className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>

            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                    Oldal: <strong>{currentPage}</strong> / <strong>{totalPages}</strong>
                </span>
                <Input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={jumpPage}
                    onChange={(e) => setJumpPage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ugrás oldalra"
                    className="w-24"
                />
                <Button onClick={handleJump} size="sm">
                    Ugrás
                </Button>
            </div>
        </div>
    );
}
